import { ExecutionResult } from 'graphql';
import { MutationResult } from '@apollo/react-common';
import { useMutation, useApolloClient } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import {
  DeleteTimelineEntryMutation,
  DeleteTimelineEntryMutationVariables,
  ListTimelineEntriesQuery,
  MeasurementSystem,
} from '../../../types/__generated__';
import {
  readTimelineEntries,
  writeTimelineEntries,
} from './useListTimelineEntries';
import { useNotNullableContext } from '../useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import { buildQuery } from '../usePageQuery';
import { mediaCollectionFragment } from '../../fragments/mediaCollection';
import { updateActivityTotals } from './updateActivityTotals';

const DELETE_ENTRY = gql`
  mutation DeleteTimelineEntry($input: DeleteTimelineEntryInput!) {
    deleteTimelineEntry(input: $input)
  }
`;

export default function useDeleteTimelineEntry(
  measurementSystem?: MeasurementSystem,
): [
  (id: string) => Promise<ExecutionResult<DeleteTimelineEntryMutation>>,
  MutationResult<DeleteTimelineEntryMutation>,
] {
  const client = useApolloClient();
  const { pageType, pageShortName, isPreview } = useNotNullableContext(
    PageMetaContext,
  );

  const [runMutation, mutationResult] = useMutation<
    DeleteTimelineEntryMutation,
    DeleteTimelineEntryMutationVariables
  >(DELETE_ENTRY, {
    update(cache, { data }) {
      if (!data || !data.deleteTimelineEntry) {
        return;
      }

      const result = readTimelineEntries(
        pageType,
        pageShortName,
        cache,
        measurementSystem,
      );

      if (!result) {
        return;
      }

      const {
        previousPage,
        previousOwner,
        previousTimeline,
        previousPageInfo,
        previousTimelineNodes,
      } = result;

      const newTimelineData: ListTimelineEntriesQuery = {
        page: {
          ...previousPage,
          owner: previousOwner,
          timeline: {
            ...previousTimeline,
            pageInfo: previousPageInfo,
            totalCount: previousTimeline.totalCount - 1,
            nodes: previousTimelineNodes.filter(
              timelineEntry => timelineEntry.id !== data.deleteTimelineEntry,
            ),
          },
        },
      };

      writeTimelineEntries(pageType, pageShortName, newTimelineData, cache);
      updateActivityTotals(client, pageType, pageShortName, isPreview);
    },
    context: {
      credentials: 'include',
    },
  });

  const deleteTimelineEntry = (id: string) =>
    runMutation({
      variables: { input: { id } },
      refetchQueries: [
        {
          query: buildQuery(mediaCollectionFragment),
          variables: {
            type: pageType.toUpperCase(),
            slug: pageShortName,
            preview: isPreview,
          },
        },
      ],
    });

  return [deleteTimelineEntry, mutationResult];
}
